/* eslint-disable import/order */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field } from "formik";
import { subYears, format, getYear } from "date-fns";
import _get from "lodash/get";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getManualAllocation } from "state/slices/manualShareAllocation";
import BoMaxValue from "./BoMaxValue";
import Input from "components/lib/Shared/Input";
import Radio from "components/lib/Shared/Radio";
import RadioGroup from "components/lib/Shared/RadioGroup";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import { roundNumber } from "utils";
import { Constants } from "config/constants";

const OfficialsBeneficialOwnershipComponent = (props) => {
  const {
    field,
    form,
    corporateIdType,
    isBOTab,
    disableChangeEffect,
    selectedShareholder,
    disableDirectFields,
    isReportable,
    isInManualUpdate,
    transactions,
    isinChangesList,
    row,
    disabled,
    // NEW: Percentage change data for showing original values
    percentageChanges,
    originalPercentages,
  } = props;

  const { companyId } = useParams();
  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );

  let resolvedFields = props?.resolvedFields
    ? props.resolvedFields
    : field.value?.resolvedFields ?? [];

  // In manual update mode, ensure all fields are editable by clearing resolvedFields
  if (isInManualUpdate) {
    resolvedFields = [];
  }

  const { company } = useSelector((state) => state.companySlice);

  const minAppointmentDate = new Date(company?.registrationDate);

  // Fixed fallback: 2020-Feb-21
  const fallbackDate = new Date(2020, 1, 21); // Month is 0-based (1 = February)

  const minDate =
    getYear(minAppointmentDate) < 2020 ? fallbackDate : minAppointmentDate;
  const {
    errors,
    touched,
    setFieldValue,
    values: { comments, beneficialOwnershipForm },
  } = form;

  const handleDateValue = (date) => {
    setFieldValue(field.name, {
      ...field.value,
      dateOfBecomingBO: format(date, "yyyy/MM/dd"),
    });
  };

  const currentFormValues = beneficialOwnershipForm || {};

  const getValue = (fieldName) => {
    if (
      isinChangesList &&
      field.value &&
      field.value[fieldName] !== undefined
    ) {
      return field.value[fieldName];
    }
    return (
      currentFormValues[fieldName] ??
      row?.original?.beneficialOwnershipForm?.[fieldName] ??
      ""
    );
  };

  const getBooleanValue = (fieldName, defaultValue = "no") => {
    if (
      isinChangesList &&
      field.value &&
      field.value[fieldName] !== undefined
    ) {
      return field.value[fieldName];
    }
    return (
      currentFormValues[fieldName] ??
      row?.original?.beneficialOwnershipForm?.[fieldName] ??
      defaultValue
    );
  };

  const maxPercentShareholding = getValue("maxPercentShareholding");
  const maxPercentVotingRights = getValue("maxPercentVotingRights");
  const indirectPercentShareholding = getValue("indirectPercentShareholding");
  const directPercentShareholding = getValue("directPercentShareholding");
  const indirectPercentVotingRights = getValue("indirectPercentVotingRights");
  const directPercentVotingRights = getValue("directPercentVotingRights");
  const directRightRemoveDirector = getBooleanValue(
    "directRightRemoveDirector",
    "no"
  );
  const indirectRightRemoveDirector = getBooleanValue(
    "indirectRightRemoveDirector",
    "no"
  );
  const directCompanyControlRight = getBooleanValue(
    "directCompanyControlRight",
    "no"
  );
  const indirectCompanyControlRight = getBooleanValue(
    "indirectCompanyControlRight",
    "no"
  );
  const dateOfBecomingBO = getValue("dateOfBecomingBO");
  const sourceOfBOInformation = getValue("sourceOfBOInformation");

  // Manual update context
  const manualTx = useMemo(() => {
    if (!isInManualUpdate || !transactions) return null;
    return transactions.transactionType === "MANUAL" ? transactions : null;
  }, [isInManualUpdate, transactions]);

  // Local state to hold calculated values for BoMaxValue
  const [calculatedMaxShareholding, setCalculatedMaxShareholding] =
    useState(null);
  const [calculatedMaxVotingRights, setCalculatedMaxVotingRights] =
    useState(null);

  const manualAllocationData = useSelector((state) =>
    getManualAllocation(state, companyId, row?.original?._id, manualTx?._id)
  );

  // Calculate original max values - prioritize percentage change data if available
  const originalMaxShareholding = useMemo(() => {
    if (!isInManualUpdate || !row?.original) return null;

    // First, try to get original percentage from percentage changes (more accurate)
    const currentOfficialId = row?.original?._id;
    if (
      originalPercentages &&
      currentOfficialId &&
      originalPercentages[currentOfficialId]
    ) {
      const originalPercentage =
        originalPercentages[currentOfficialId].totalPercentage;

      return roundNumber(originalPercentage, 2);
    }

    // Fallback to beneficial ownership form calculation
    const beneficialOwnershipForm = row.original.beneficialOwnershipForm;
    if (!beneficialOwnershipForm) {
      return null;
    }

    const direct = Number(
      beneficialOwnershipForm.directPercentShareholding || 0
    );
    const indirect = Number(
      beneficialOwnershipForm.indirectPercentShareholding || 0
    );
    const calculated = roundNumber(direct + indirect, 2);

    return calculated;
  }, [isInManualUpdate, row?.original, originalPercentages]);

  const originalMaxVotingRights = useMemo(() => {
    if (!isInManualUpdate || !row?.original) return null;

    // Add null check for beneficialOwnershipForm
    const beneficialOwnershipForm = row.original.beneficialOwnershipForm;
    if (!beneficialOwnershipForm) {
      return null;
    }

    return roundNumber(
      Number(beneficialOwnershipForm.directPercentVotingRights || 0) +
        Number(beneficialOwnershipForm.indirectPercentVotingRights || 0),
      2
    );
  }, [isInManualUpdate, row?.original]);

  // -------------------- PREFILL (previous saved data) --------------------
  const lastPrefilledRowIdRef = useRef(null);
  useEffect(() => {
    const rowId = row?.original?._id ?? "__no_row__";
    if (lastPrefilledRowIdRef.current === rowId) return;
    if (isinChangesList && field.value) {
      const prefillData = {
        maxPercentShareholding: field.value.maxPercentShareholding ?? "",
        maxPercentVotingRights: field.value.maxPercentVotingRights ?? "",
        directPercentShareholding: field.value.directPercentShareholding ?? "",
        indirectPercentShareholding:
          field.value.indirectPercentShareholding ?? "",
        directPercentVotingRights: field.value.directPercentVotingRights ?? "",
        indirectPercentVotingRights:
          field.value.indirectPercentVotingRights ?? "",
        directRightRemoveDirector:
          field.value.directRightRemoveDirector ?? "no",
        indirectRightRemoveDirector:
          field.value.indirectRightRemoveDirector ?? "no",
        directCompanyControlRight:
          field.value.directCompanyControlRight ?? "no",
        indirectCompanyControlRight:
          field.value.indirectCompanyControlRight ?? "no",
        dateOfBecomingBO: field.value.dateOfBecomingBO ?? "",
        sourceOfBOInformation: field.value.sourceOfBOInformation ?? "",
        resolvedFields: field.value.resolvedFields ?? [],
      };

      setFieldValue("beneficialOwnershipForm", prefillData);
    } else if (isInManualUpdate && row?.original) {
      const originalBOForm = row.original.beneficialOwnershipForm || {};
      const prefillData = {
        maxPercentShareholding: originalBOForm.maxPercentShareholding,
        maxPercentVotingRights: originalBOForm.maxPercentVotingRights,
        directPercentShareholding: originalBOForm.directPercentShareholding,
        indirectPercentShareholding: originalBOForm.indirectPercentShareholding,
        directPercentVotingRights: originalBOForm.directPercentVotingRights,
        indirectPercentVotingRights: originalBOForm.indirectPercentVotingRights,
        directRightRemoveDirector: originalBOForm.directRightRemoveDirector,
        indirectRightRemoveDirector: originalBOForm.indirectRightRemoveDirector,
        directCompanyControlRight: originalBOForm.directCompanyControlRight,
        indirectCompanyControlRight: originalBOForm.indirectCompanyControlRight,
        dateOfBecomingBO: originalBOForm.dateOfBecomingBO,
        sourceOfBOInformation: originalBOForm.sourceOfBOInformation,
        // In manual update mode, clear resolvedFields to ensure all fields are editable
        resolvedFields: [],
      };

      setFieldValue("beneficialOwnershipForm", prefillData);
    } else {
      // Standard prefill with row.original.beneficialOwnershipForm data (non-manual update mode)
      const originalBOForm = row?.original?.beneficialOwnershipForm || {};
      setFieldValue("beneficialOwnershipForm", {
        maxPercentShareholding: originalBOForm.maxPercentShareholding ?? "",
        maxPercentVotingRights: originalBOForm.maxPercentVotingRights ?? "",
        directPercentShareholding:
          originalBOForm.directPercentShareholding ?? "",
        indirectPercentShareholding:
          originalBOForm.indirectPercentShareholding ?? "",
        directPercentVotingRights:
          originalBOForm.directPercentVotingRights ?? "",
        indirectPercentVotingRights:
          originalBOForm.indirectPercentVotingRights ?? "",
        directRightRemoveDirector:
          originalBOForm.directRightRemoveDirector ?? "no",
        indirectRightRemoveDirector:
          originalBOForm.indirectRightRemoveDirector ?? "no",
        directCompanyControlRight:
          originalBOForm.directCompanyControlRight ?? "no",
        indirectCompanyControlRight:
          originalBOForm.indirectCompanyControlRight ?? "no",
        dateOfBecomingBO: originalBOForm.dateOfBecomingBO ?? "",
        sourceOfBOInformation: originalBOForm.sourceOfBOInformation ?? "",
        resolvedFields: row?.original?.resolvedFields ?? [],
      });
    }

    lastPrefilledRowIdRef.current = rowId;
  }, [row, setFieldValue, isInManualUpdate, isinChangesList, field.value]);

  // -------------------- COMPUTE MAX PERCENTAGES --------------------
  useEffect(() => {
    // For non-manual update mode, calculate max shareholding based on direct + indirect
    if (!isInManualUpdate) {
      // Calculate max shareholding as sum of direct and indirect
      const direct = Number(directPercentShareholding || 0);
      const indirect = Number(indirectPercentShareholding || 0);
      const calculatedMax = roundNumber(direct + indirect, 2);

      // Set the calculated values in local state for BoMaxValue
      setCalculatedMaxShareholding(calculatedMax);
      setCalculatedMaxVotingRights(
        roundNumber(
          Number(directPercentVotingRights || 0) +
            Number(indirectPercentVotingRights || 0),
          2
        )
      );
      return;
    }

    // Initial computation: Use sum of direct + indirect from row.original as initial max values
    if (row?.original && !manualAllocationData) {
      const initialMaxShareholding = originalMaxShareholding;
      const initialMaxVotingRights = originalMaxVotingRights;

      // Set the calculated values in local state for BoMaxValue
      setCalculatedMaxShareholding(initialMaxShareholding);
      setCalculatedMaxVotingRights(initialMaxVotingRights);

      setFieldValue(
        "beneficialOwnershipForm.maxPercentShareholding",
        initialMaxShareholding
      );
      setFieldValue(
        "beneficialOwnershipForm.maxPercentVotingRights",
        initialMaxVotingRights
      );
      return;
    }

    // Recomputation when manualAllocationData changes
    if (manualTx && manualAllocationData) {
      // Step 1: Company current total shares number
      const currentCompanySharesNumber = nominalShareCapital.reduce(
        (acc, share) => {
          const shares = Number(share.sharesNumber || 0);
          return acc + shares;
        },
        0
      );

      // Step 2: Company current total voting rights
      const currentCompanyVotingRights = nominalShareCapital.reduce(
        (acc, share) => {
          const votes =
            Number(share.votesPerShare || 0) > 0
              ? Number(share.sharesNumber || 0) *
                Number(share.votesPerShare || 0)
              : 0;
          return acc + votes;
        },
        0
      );

      const userCurrentSharesNumber = (
        row?.original?.sharesAllotted || []
      ).reduce((sum, share) => {
        return sum + Number(share.allottedShares || 0);
      }, 0);

      const userCurrentVotingRights = (
        row?.original?.sharesAllotted || []
      ).reduce((sum, share) => {
        return sum + Number(share.votingRights || 0);
      }, 0);

      // Step 4: Shares on offer & user's new allocations
      let sharesOnOfferNumber = 0;
      let sharesOnOfferVotingRights = 0;
      let userNewAllocationSharesNumber = 0;
      let userNewAllocationVotingRights = 0;

      // Shares on offer from transaction
      if (manualTx.shareCapitalChanges) {
        manualTx.shareCapitalChanges.forEach((change) => {
          const votesPerShare =
            nominalShareCapital.find(
              (ns) => ns.sharesCategory === change.classOfShares
            )?.votesPerShare || 0;

          sharesOnOfferNumber += Number(change.sharesOnOffer || 0);

          if (Number(votesPerShare) > 0) {
            sharesOnOfferVotingRights +=
              Number(change.sharesOnOffer || 0) * Number(votesPerShare || 0);
          }
        });
      }

      // User's new allocations from manualAllocationData
      const allocationSource = manualAllocationData.shareCapitalChanges || [];
      allocationSource.forEach((change) => {
        const votesPerShare =
          nominalShareCapital.find(
            (ns) => ns.sharesCategory === change.classOfShares
          )?.votesPerShare || 0;

        const userAllocations = (change.allocations || []).filter(
          (alloc) => String(alloc.officialId) === String(row?.original?._id)
        );

        const userAllocationShares = userAllocations.reduce((sum, alloc) => {
          return sum + Number(alloc.allocatedShares || 0);
        }, 0);
        userNewAllocationSharesNumber += userAllocationShares;

        const userAllocationVoting = userAllocations.reduce((sum, alloc) => {
          return (
            sum +
            Number(alloc.allocatedShares || 0) * Number(votesPerShare || 0)
          );
        }, 0);
        userNewAllocationVotingRights += userAllocationVoting;
      });

      // Step 5: Apply the formulas
      // Formula: (user current shares + user new allocation shares) / (company current shares + shares on offer) * 100
      const totalUserSharesNumber =
        Number(userCurrentSharesNumber || 0) +
        Number(userNewAllocationSharesNumber || 0);
      const totalCompanySharesNumber =
        Number(currentCompanySharesNumber || 0) +
        Number(sharesOnOfferNumber || 0);

      const totalUserVotingRights =
        Number(userCurrentVotingRights || 0) +
        Number(userNewAllocationVotingRights || 0);
      const totalCompanyVotingRights =
        Number(currentCompanyVotingRights || 0) +
        Number(sharesOnOfferVotingRights || 0);

      const newMaxPercentShareholding =
        totalCompanySharesNumber > 0
          ? roundNumber(
              (totalUserSharesNumber / totalCompanySharesNumber) * 100,
              2
            )
          : 0;

      const newMaxPercentVotingRights =
        totalCompanyVotingRights > 0
          ? roundNumber(
              (totalUserVotingRights / totalCompanyVotingRights) * 100,
              2
            )
          : 0;

      // Set the calculated values in local state for BoMaxValue
      setCalculatedMaxShareholding(newMaxPercentShareholding);
      setCalculatedMaxVotingRights(newMaxPercentVotingRights);

      setFieldValue(
        "beneficialOwnershipForm.maxPercentShareholding",
        newMaxPercentShareholding
      );
      setFieldValue(
        "beneficialOwnershipForm.maxPercentVotingRights",
        newMaxPercentVotingRights
      );
    } else {
      // When NOT in manual update mode, check if we have percentage change data to display
      const currentOfficialId = row?.original?._id;
      if (
        percentageChanges &&
        currentOfficialId &&
        percentageChanges[currentOfficialId]
      ) {
        const newPercentage =
          percentageChanges[currentOfficialId].newPercentage;

        setCalculatedMaxShareholding(newPercentage);
        setFieldValue(
          "beneficialOwnershipForm.maxPercentShareholding",
          newPercentage
        );
      }
    }

    // SPECIAL CASE: When in manual update mode, use the NEW percentage as the working max
    if (isInManualUpdate) {
      const currentOfficialId = row?.original?._id;
      if (
        percentageChanges &&
        currentOfficialId &&
        percentageChanges[currentOfficialId]
      ) {
        const newPercentage =
          percentageChanges[currentOfficialId].newPercentage;

        setCalculatedMaxShareholding(newPercentage);
        setFieldValue(
          "beneficialOwnershipForm.maxPercentShareholding",
          newPercentage
        );

        // Also set voting rights to the same new percentage for consistency
        setCalculatedMaxVotingRights(newPercentage);
        setFieldValue(
          "beneficialOwnershipForm.maxPercentVotingRights",
          newPercentage
        );
      }
    }
  }, [
    isInManualUpdate,
    manualTx,
    manualAllocationData,
    row,
    nominalShareCapital,
    setFieldValue,
    originalMaxShareholding,
    originalMaxVotingRights,
    companyId,
    percentageChanges,
    originalPercentages,
  ]);

  // Only show loading if we're in manual update mode and essential data is missing
  // if (isInManualUpdate && !row?.original?.beneficialOwnershipForm) {
  //   return <div>Loading beneficial ownership form data...</div>;
  // }

  const options = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const sourceOfBOInformationOptions = Constants.SOURCE_OF_BO_INFO_OPTIONS;

  const selectedDirectRightRemoveDirector = options.find(
    (option) => option.value === directRightRemoveDirector
  );
  const selectedIndirectRightRemoveDirector = options.find(
    (option) => option.value === indirectRightRemoveDirector
  );
  const selectedDirectCompanyControlRight = options.find(
    (option) => option.value === directCompanyControlRight
  );
  const selectedIndirectCompanyControlRight = options.find(
    (option) => option.value === indirectCompanyControlRight
  );
  const selectedSourceOfBOInformation = sourceOfBOInformationOptions.find(
    (option) => option.value === sourceOfBOInformation
  );

  const handleChange = ({ target: { value, name, max }, currentTarget }) => {
    const numericValue = Number(value);
    const numericMax = Number(max);

    // Validate that the value doesn't exceed the maximum
    const validValue =
      numericMax && numericValue > numericMax ? numericMax : numericValue;

    setFieldValue(name, roundNumber(validValue));

    // Apply the change effect to the paired field if enabled and valid
    if (!disableChangeEffect && !Number.isNaN(numericMax) && currentTarget) {
      const targetField = currentTarget.getAttribute("applychangeeffectto");
      if (targetField) {
        const remainingValue = roundNumber(numericMax - validValue);
        setFieldValue(targetField, remainingValue >= 0 ? remainingValue : 0);
      }
    }
  };

  return (
    <div className="grid gap-4">
      <div className="space-y-2">
        <h3 className="font-medium">
          1. What percentage of the shareholding do you hold in the company?
        </h3>
        <div className="grid gap-2">
          <BoMaxValue
            value={
              calculatedMaxShareholding !== null
                ? calculatedMaxShareholding
                : maxPercentShareholding
            }
            oldValue={
              // In manual update mode, show original percentage as "before" value
              isInManualUpdate &&
              percentageChanges &&
              row?.original?._id &&
              percentageChanges[row.original._id]
                ? percentageChanges[row.original._id].originalPercentage
                : originalMaxShareholding
            }
            isInManualUpdate={
              isInManualUpdate ||
              (percentageChanges &&
                row?.original?._id &&
                percentageChanges[row.original._id])
            }
          />

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <Field
                name="beneficialOwnershipForm.directPercentShareholding"
                type="number"
                component={Input}
                placeholder="Eg. 10%"
                label="Directly"
                showRequiredAsterisk
                errorPath="beneficialOwnershipForm.directPercentShareholding"
                commentPath="beneficialOwnershipForm.percentShareholding"
                onChange={handleChange}
                value={directPercentShareholding.toString()}
                min={0}
                max={
                  isBOTab
                    ? (calculatedMaxShareholding !== null
                        ? calculatedMaxShareholding
                        : maxPercentShareholding) - indirectPercentShareholding
                    : calculatedMaxShareholding !== null
                    ? calculatedMaxShareholding
                    : maxPercentShareholding
                }
                step="0.01"
                applychangeeffectto="beneficialOwnershipForm.indirectPercentShareholding"
                disabled={
                  isinChangesList
                    ? false
                    : corporateIdType === "Company Limited By Guarantee/NGO" ||
                      disableDirectFields ||
                      (calculatedMaxShareholding !== null
                        ? calculatedMaxShareholding
                        : maxPercentShareholding) === 0 ||
                      resolvedFields.includes("directPercentShareholding") ||
                      disabled
                }
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Field
                component={Input}
                label="Indirectly"
                showRequiredAsterisk
                name="beneficialOwnershipForm.indirectPercentShareholding"
                type="number"
                placeholder="Eg. 10%"
                errorPath="beneficialOwnershipForm.indirectPercentShareholding"
                commentPath=""
                onChange={handleChange}
                value={indirectPercentShareholding.toString()}
                min={0}
                max={
                  isBOTab
                    ? (calculatedMaxShareholding !== null
                        ? calculatedMaxShareholding
                        : maxPercentShareholding) - directPercentShareholding
                    : calculatedMaxShareholding !== null
                    ? calculatedMaxShareholding
                    : maxPercentShareholding
                }
                step="0.01"
                applychangeeffectto="beneficialOwnershipForm.directPercentShareholding"
                disabled={
                  isinChangesList
                    ? false
                    : corporateIdType === "Company Limited By Guarantee/NGO" ||
                      (calculatedMaxShareholding !== null
                        ? calculatedMaxShareholding
                        : maxPercentShareholding) === 0 ||
                      resolvedFields.includes("indirectPercentShareholding") ||
                      disabled
                }
              />
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div className="space-y-2">
        <h3 className="font-medium">
          2. What percentage of the voting rights do you exercise in the
          company?
        </h3>
        <div className="grid gap-2">
          <BoMaxValue
            value={
              calculatedMaxVotingRights !== null
                ? calculatedMaxVotingRights
                : maxPercentVotingRights
            }
            oldValue={
              // In manual update mode, show original voting rights percentage as "before" value
              isInManualUpdate &&
              percentageChanges &&
              row?.original?._id &&
              percentageChanges[row.original._id]
                ? percentageChanges[row.original._id].originalPercentage
                : originalMaxVotingRights
            }
            isInManualUpdate={
              isInManualUpdate ||
              (percentageChanges &&
                row?.original?._id &&
                percentageChanges[row.original._id])
            }
          />

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <Field
                component={Input}
                label="Directly"
                showRequiredAsterisk
                name="beneficialOwnershipForm.directPercentVotingRights"
                errorPath="beneficialOwnershipForm.directPercentVotingRights"
                commentPath="beneficialOwnershipForm.percentVotingRights"
                type="number"
                onChange={handleChange}
                value={directPercentVotingRights.toString()}
                min={0}
                max={
                  isBOTab
                    ? (calculatedMaxVotingRights !== null
                        ? calculatedMaxVotingRights
                        : maxPercentVotingRights) - indirectPercentVotingRights
                    : calculatedMaxVotingRights !== null
                    ? calculatedMaxVotingRights
                    : maxPercentVotingRights
                }
                step="0.01"
                applychangeeffectto="beneficialOwnershipForm.indirectPercentVotingRights"
                placeholder="Eg. 10%"
                disabled={
                  isinChangesList
                    ? false
                    : disableDirectFields ||
                      (calculatedMaxVotingRights !== null
                        ? calculatedMaxVotingRights
                        : maxPercentVotingRights) === 0 ||
                      resolvedFields.includes("directPercentVotingRights") ||
                      disabled
                }
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Field
                component={Input}
                label="Indirectly"
                showRequiredAsterisk
                type="number"
                name="beneficialOwnershipForm.indirectPercentVotingRights"
                errorPath="beneficialOwnershipForm.indirectPercentVotingRights"
                commentPath=""
                onChange={handleChange}
                value={indirectPercentVotingRights.toString()}
                min={0}
                max={
                  isBOTab
                    ? (calculatedMaxVotingRights !== null
                        ? calculatedMaxVotingRights
                        : maxPercentVotingRights) - directPercentVotingRights
                    : calculatedMaxVotingRights !== null
                    ? calculatedMaxVotingRights
                    : maxPercentVotingRights
                }
                step="0.01"
                applychangeeffectto="beneficialOwnershipForm.directPercentVotingRights"
                placeholder="Eg. 10%"
                disabled={
                  isinChangesList
                    ? false
                    : (calculatedMaxVotingRights !== null
                        ? calculatedMaxVotingRights
                        : maxPercentVotingRights) === 0 ||
                      resolvedFields.includes("indirectPercentVotingRights") ||
                      disabled
                }
              />
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div className="space-y-2">
        <h3 className="font-medium">
          3. Do you have a right to appoint/remove a director in the company?
        </h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <RadioGroup
              label="Directly"
              showRequiredAsterisk
              errors={errors}
              touched={touched}
              comments={_get(
                comments,
                "beneficialOwnershipForm.rightRemoveDirector",
                []
              )}
              name="beneficialOwnershipForm.directRightRemoveDirector"
            >
              <Radio
                name="beneficialOwnershipForm.directRightRemoveDirector"
                id="yesDirectRightRemoveDirector"
                label="Yes"
                value="yes"
                checked={directRightRemoveDirector === "yes"}
                disabled={
                  isinChangesList
                    ? false
                    : disableDirectFields ||
                      resolvedFields.includes("directRightRemoveDirector") ||
                      disabled
                }
              />
              <Radio
                name="beneficialOwnershipForm.directRightRemoveDirector"
                id="nodirectRightRemoveDirector"
                label="No"
                value="no"
                checked={directRightRemoveDirector === "no"}
                disabled={
                  isinChangesList
                    ? false
                    : disableDirectFields ||
                      resolvedFields.includes("directRightRemoveDirector") ||
                      disabled
                }
              />
            </RadioGroup>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <RadioGroup
              label="Indirectly"
              showRequiredAsterisk
              errors={errors}
              touched={touched}
              name="beneficialOwnershipForm.indirectRightRemoveDirector"
            >
              <Radio
                name="beneficialOwnershipForm.indirectRightRemoveDirector"
                id="yesIndirectRightRemoveDirector"
                label="Yes"
                value="yes"
                checked={indirectRightRemoveDirector === "yes"}
                disabled={
                  isinChangesList
                    ? false
                    : selectedShareholder?.beneficialOwnershipForm
                        ?.indirectRightRemoveDirector === "yes" ||
                      resolvedFields.includes("indirectRightRemoveDirector") ||
                      disabled
                }
              />
              <Radio
                name="beneficialOwnershipForm.indirectRightRemoveDirector"
                id="noIndirectRightRemoveDirector"
                label="No"
                value="no"
                checked={indirectRightRemoveDirector === "no"}
                disabled={
                  isinChangesList
                    ? false
                    : selectedShareholder?.beneficialOwnershipForm
                        ?.indirectRightRemoveDirector === "no" ||
                      resolvedFields.includes("indirectRightRemoveDirector") ||
                      disabled
                }
              />
            </RadioGroup>
          </div>
        </div>
      </div>
      <hr />
      <div className="space-y-2">
        <h3 className="font-medium">
          4. Do you exercise significant influence or control over the company?
        </h3>
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-3">
            <RadioGroup
              label="Directly"
              showRequiredAsterisk
              errors={errors}
              touched={touched}
              comments={_get(
                comments,
                "beneficialOwnershipForm.companyControlRight",
                []
              )}
              name="beneficialOwnershipForm.directCompanyControlRight"
            >
              <Radio
                name="beneficialOwnershipForm.directCompanyControlRight"
                id="yesDirectCompanyControlRight"
                label="Yes"
                value="yes"
                checked={directCompanyControlRight === "yes"}
                disabled={
                  isinChangesList
                    ? false
                    : disableDirectFields ||
                      resolvedFields.includes("directCompanyControlRight") ||
                      disabled
                }
              />
              <Radio
                name="beneficialOwnershipForm.directCompanyControlRight"
                id="noDirectCompanyControlRight"
                label="No"
                value="no"
                checked={directCompanyControlRight === "no"}
                disabled={
                  isinChangesList
                    ? false
                    : disableDirectFields ||
                      resolvedFields.includes("directCompanyControlRight") ||
                      disabled
                }
              />
            </RadioGroup>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <RadioGroup
              label="Indirectly"
              showRequiredAsterisk
              errors={errors}
              touched={touched}
              name="beneficialOwnershipForm.indirectCompanyControlRight"
            >
              <Radio
                name="beneficialOwnershipForm.indirectCompanyControlRight"
                id="yesIndirectCompanyControlRight"
                label="Yes"
                value="yes"
                checked={indirectCompanyControlRight === "yes"}
                disabled={
                  isinChangesList
                    ? false
                    : selectedShareholder?.beneficialOwnershipForm
                        ?.indirectCompanyControlRight === "no" ||
                      resolvedFields.includes("indirectCompanyControlRight") ||
                      disabled
                }
              />
              <Radio
                name="beneficialOwnershipForm.indirectCompanyControlRight"
                id="noIndirectCompanyControlRight"
                label="No"
                value="no"
                checked={indirectCompanyControlRight === "no"}
                disabled={
                  isinChangesList
                    ? false
                    : selectedShareholder?.beneficialOwnershipForm
                        ?.indirectCompanyControlRight === "no" ||
                      resolvedFields.includes("indirectCompanyControlRight") ||
                      disabled
                }
              />
            </RadioGroup>
          </div>
        </div>
      </div>
      <hr />
      <div className="grid grid-cols-6 gap-6">
        <div className="col-span-6 sm:col-span-3">
          <DatePickerField
            label="Effective Date Of Becoming B.O"
            showRequiredAsterisk={isReportable}
            selected={dateOfBecomingBO ? new Date(dateOfBecomingBO) : ""}
            onChange={handleDateValue}
            minDate={minDate}
            maxDate={new Date()}
            name="beneficialOwnershipForm.dateOfBecomingBO"
            form={form}
            placeholderText="Select date of becoming B.O"
            errorPath="beneficialOwnershipForm.dateOfBecomingBO"
            disabled={
              isinChangesList
                ? false
                : isReportable === false ||
                  resolvedFields.includes("dateOfBecomingBO") ||
                  disabled
            }
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field
            label="Source of beneficial owner information"
            showRequiredAsterisk
            value={selectedSourceOfBOInformation}
            name="beneficialOwnershipForm.sourceOfBOInformation"
            component={ReactSelectWithFormik}
            options={sourceOfBOInformationOptions}
            errorPath="beneficialOwnershipForm.sourceOfBOInformation"
            placeholder="Select source of beneficial owner information"
            isDisabled={
              resolvedFields.includes("sourceOfBOInformation") || disabled
            }
          />
        </div>
      </div>
    </div>
  );
};

export default OfficialsBeneficialOwnershipComponent;
